import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, MapPin, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/appresse";

// Reusable "postal code -> city" picker backed by the bundled GeoNames database.
// Default country = France. When several cities share a code, the user picks one.
export function PostalCity({ value, onChange, idPrefix = "loc" }) {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState(null); // null idle, [] not found, [..]
  const [loading, setLoading] = useState(false);
  const debRef = useRef(null);

  const countryCode = value.countryCode || "FR";
  const postal = value.postal || "";
  const city = value.city || "";

  useEffect(() => {
    api.geoCountries().then((d) => setCountries(d.countries || [])).catch(() => setCountries([]));
  }, []);

  const countryName = useCallback(
    (code) => (countries.find((c) => c.code === code) || {}).name || "France",
    [countries]
  );

  const lookup = useCallback((code, pc) => {
    if (!pc || pc.trim().length < 2) { setCities(null); return; }
    setLoading(true);
    api.geoCity(pc.trim(), code)
      .then((d) => {
        const list = d.cities || [];
        setCities(list);
        // Auto-select when a single city matches; keep current if still valid.
        if (list.length >= 1) {
          const keep = list.includes(city) ? city : list[0];
          onChange({ countryCode: code, country: countryName(code), postal: pc, city: keep });
        } else {
          onChange({ countryCode: code, country: countryName(code), postal: pc, city: "" });
        }
      })
      .catch(() => setCities([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, countryName]);

  const onPostal = (pc) => {
    onChange({ countryCode, country: countryName(countryCode), postal: pc, city: "" });
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => lookup(countryCode, pc), 350);
  };

  const onCountry = (code) => {
    onChange({ countryCode: code, country: countryName(code), postal, city: "" });
    setCities(null);
    if (postal) { clearTimeout(debRef.current); debRef.current = setTimeout(() => lookup(code, postal), 200); }
  };

  const onCity = (c) => onChange({ countryCode, country: countryName(countryCode), postal, city: c });

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-postalcity`}>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-country`}>Pays</Label>
        <Select value={countryCode} onValueChange={onCountry}>
          <SelectTrigger id={`${idPrefix}-country`} data-testid={`${idPrefix}-country-select`}>
            <SelectValue placeholder="Pays" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code} data-testid={`${idPrefix}-country-${c.code}`}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-postal`}>Code postal</Label>
          <div className="relative">
            <Input
              id={`${idPrefix}-postal`}
              value={postal}
              onChange={(e) => onPostal(e.target.value)}
              placeholder="Ex. 75001"
              autoComplete="postal-code"
              data-testid={`${idPrefix}-postal-input`}
              className="pr-8"
            />
            {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-city`}>Ville</Label>
          {Array.isArray(cities) && cities.length > 1 ? (
            <Select value={city} onValueChange={onCity}>
              <SelectTrigger id={`${idPrefix}-city`} data-testid={`${idPrefix}-city-select`}>
                <SelectValue placeholder="Choisir…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {cities.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`${idPrefix}-city-opt`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id={`${idPrefix}-city`}
              value={city}
              onChange={(e) => onCity(e.target.value)}
              placeholder="Ville"
              data-testid={`${idPrefix}-city-input`}
              readOnly={Array.isArray(cities) && cities.length === 1}
            />
          )}
        </div>
      </div>

      {Array.isArray(cities) && cities.length > 1 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid={`${idPrefix}-multi-hint`}>
          <MapPin className="w-3.5 h-3.5 text-primary" /> Plusieurs villes ont ce code postal — choisissez la vôtre.
        </p>
      )}
      {Array.isArray(cities) && cities.length === 0 && postal.trim().length >= 2 && !loading && (
        <p className="text-xs text-amber-600 flex items-center gap-1" data-testid={`${idPrefix}-notfound-hint`}>
          Code postal introuvable — saisissez votre ville manuellement.
        </p>
      )}
      {city && Array.isArray(cities) && cities.length === 1 && (
        <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid={`${idPrefix}-ok-hint`}>
          <Check className="w-3.5 h-3.5" /> {city}
        </p>
      )}
    </div>
  );
}
